import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  LinearProgress,
  RadioGroup,
  Card,
  CardContent,
  Collapse,
  TextField,
  Grid,
} from "@mui/material";
import {
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { RichTextDisplay, PreviewAudioButton } from "@/components/common";
import {
  useGetStudentVocabularyQuestionsQuery,
  useSubmitAnswerMutation,
} from "@/store/api/user/exerciseApi";
import { toast } from "react-toastify";

// ─── Sub-component: Question Media (images + audio) ─────────────────────────
const QuestionMedia = ({ question }) => {
  const { imageUrl, extraImageUrl, audioUrl } = question;

  const hasMedia = imageUrl || extraImageUrl || audioUrl;
  if (!hasMedia) return null;

  const hasImages = imageUrl || extraImageUrl;
  const imageCount = [imageUrl, extraImageUrl].filter(Boolean).length;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Images */}
      {hasImages && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
            mb: audioUrl ? 2 : 0,
          }}
        >
          {[imageUrl, extraImageUrl].filter(Boolean).map((url, idx) => (
            <Box
              key={idx}
              sx={{
                flex: imageCount === 1 ? "0 1 auto" : "0 1 48%",
                maxWidth: imageCount === 1 ? 400 : "48%",
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
              }}
            >
              <Box
                component="img"
                src={url}
                alt={`Question image ${idx + 1}`}
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: 300,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── Utility: render fill-blank question text with inline inputs ───────────
const renderFillBlankQuestion = (
  questionText,
  question,
  answers,
  showResults,
  onFillBlankChange,
) => {
  if (!questionText) return null;

  const parts = [];
  let lastIndex = 0;
  const regex = /\{\{(\d+)\}\}/g;
  let match;

  // Split text by blank markers, preserving HTML
  while ((match = regex.exec(questionText)) !== null) {
    // Add HTML text before the blank
    if (match.index > lastIndex) {
      parts.push({
        type: "html",
        content: questionText.substring(lastIndex, match.index),
      });
    }

    // Add the blank input
    parts.push({
      type: "blank",
      position: parseInt(match[1]),
    });

    lastIndex = regex.lastIndex;
  }

  // Add remaining HTML text
  if (lastIndex < questionText.length) {
    parts.push({
      type: "html",
      content: questionText.substring(lastIndex),
    });
  }

  const questionId = question._id;
  const isAnswered = showResults[questionId];

  return (
    <Box
      sx={{
        lineHeight: 2.5,
        fontSize: "1.1rem",
        "& p": {
          display: "inline",
          margin: 0,
          padding: 0,
        },
        "& br": {
          display: "none",
        },
      }}
    >
      {parts.map((part, idx) => {
        if (part.type === "html") {
          return (
            <span
              key={idx}
              dangerouslySetInnerHTML={{ __html: part.content }}
            />
          );
        } else {
          // Render input box for blank
          const blank = question.blanks?.find(
            (b) => b.position === part.position,
          );
          const userAnswer = answers[questionId]?.[part.position] || "";
          const isCorrect =
            isAnswered &&
            blank?.correctAnswers?.some(
              (ans) =>
                ans.toLowerCase().trim() === userAnswer.toLowerCase().trim(),
            );
          const isIncorrect = isAnswered && !isCorrect && userAnswer;

          // Get first character hint if enabled
          const showHint =
            blank?.firstCharacterHint && !isAnswered && !userAnswer;
          const hintText =
            showHint && blank?.correctAnswers?.[0]
              ? `${blank.correctAnswers[0].charAt(0).toUpperCase()}...`
              : `(${part.position})`;

          return (
            <TextField
              key={idx}
              value={userAnswer}
              onChange={(e) =>
                onFillBlankChange(questionId, part.position, e.target.value)
              }
              disabled={isAnswered}
              placeholder={hintText}
              size="small"
              inputProps={{ autoComplete: "new-password" }}
              sx={{
                display: "inline-flex",
                verticalAlign: "middle",
                mx: "2px",
                width: userAnswer
                  ? `${Math.max(
                      60,
                      Math.min(200, userAnswer.length * 12 + 30),
                    )}px`
                  : "80px",
                maxWidth: "200px",
                "& .MuiInputBase-root": {
                  backgroundColor: isAnswered
                    ? isCorrect
                      ? "#e8f5e9"
                      : isIncorrect
                      ? "#ffebee"
                      : "#f5f5f5"
                    : "white",
                  borderRadius: "4px",
                  fontSize: "1rem",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderWidth: "1px",
                  borderColor: isAnswered
                    ? isCorrect
                      ? "#4caf50 !important"
                      : isIncorrect
                      ? "#f44336 !important"
                      : "#bdbdbd !important"
                    : "#1976d2",
                },
                "& .MuiInputBase-input": {
                  padding: "4px 8px",
                  textAlign: "center",
                  fontSize: "1rem",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isAnswered ? undefined : "#1565c0",
                },
              }}
            />
          );
        }
      })}
    </Box>
  );
};

// ─── Sub-component: Fill-Blank Question ──────────────────────────────────────
const FillBlankQuestion = ({
  question,
  answers,
  showResults,
  onFillBlankChange,
  onCheckAnswer,
}) => {
  const questionId = question._id;
  const isAnswered = showResults[questionId];

  return (
    <Box sx={{ mt: 2 }}>
      {/* Display question with inline input boxes */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: "#fafafa",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
        }}
      >
        {renderFillBlankQuestion(
          question.content,
          question,
          answers,
          showResults,
          onFillBlankChange,
        )}
      </Paper>

      {/* Show correct answers when in result mode */}
      {isAnswered && question.blanks && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Correct Answers:
          </Typography>
          <Grid container spacing={1}>
            {question.blanks
              ?.slice()
              .sort((a, b) => a.position - b.position)
              .map((blank) => {
                const userAnswer = answers[questionId]?.[blank.position] || "";
                const isCorrect = blank.correctAnswers?.some(
                  (ans) =>
                    ans.toLowerCase().trim() ===
                    userAnswer.toLowerCase().trim(),
                );

                return (
                  <Grid item xs={12} sm={6} md={4} key={blank.position}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: isCorrect ? "#e8f5e9" : "#ffebee",
                        border: "1px solid",
                        borderColor: isCorrect ? "#4caf50" : "#f44336",
                      }}
                    >
                      <Typography variant="caption" fontWeight={600}>
                        ({blank.position}) {isCorrect ? "✓" : "✗"}{" "}
                        {blank.correctAnswers?.join(", ")}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </Box>
      )}

      {/* Check Answers Button */}
      {!isAnswered &&
        question.blanks?.some(
          (blank) => answers[questionId]?.[blank.position],
        ) && (
          <Button
            variant="contained"
            onClick={() => onCheckAnswer(questionId)}
            sx={{ mt: 2 }}
            fullWidth
          >
            Check Answers
          </Button>
        )}
    </Box>
  );
};

// ─── Sub-component: Short Answer Question ────────────────────────────────────
const ShortAnswerQuestion = ({
  question,
  answers,
  showResults,
  onAnswerChange,
  onCheckAnswer,
}) => {
  const questionId = question._id;
  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Your Answer"
        placeholder="Type your answer here..."
        value={answers[questionId] || ""}
        onChange={(e) =>
          onAnswerChange(questionId, e.target.value, "short_answer")
        }
        disabled={showResults[questionId]}
        variant="outlined"
      />
      {!showResults[questionId] && answers[questionId] && (
        <Button
          variant="contained"
          onClick={() => onCheckAnswer(questionId)}
          sx={{ mt: 2 }}
          fullWidth
        >
          Check Answer
        </Button>
      )}
    </Box>
  );
};

// ─── Sub-component: Multiple Choice Question ─────────────────────────────────
const MultipleChoiceQuestion = ({
  question,
  answers,
  showResults,
  apiValidationResults,
  isSubmitting,
  onAnswerChange,
}) => {
  const questionId = question._id;
  const isAnswered = showResults[questionId];
  const selectedAnswer = answers[questionId];
  const validationResult = apiValidationResults[questionId];
  const isAnswerCorrect = validationResult?.isCorrect || false;
  const correctAnswerFromAPI = validationResult?.correctAnswer;

  return (
    <RadioGroup
      value={selectedAnswer || ""}
      onChange={(e) => onAnswerChange(questionId, e.target.value)}
      sx={{ width: "100%" }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {question.choices?.map((option, idx) => {
          const optionChar = String.fromCharCode(65 + idx);
          const isSelected = selectedAnswer === option.value;
          const isCorrectOption = correctAnswerFromAPI
            ? option.value === correctAnswerFromAPI
            : option.value === question.correctAnswer || option.isCorrect;
          const showCorrect = isAnswered && isAnswerCorrect && isSelected;
          const showWrong = isAnswered && !isAnswerCorrect && isSelected;
          const highlightCorrect =
            isAnswered && !isAnswerCorrect && isCorrectOption;

          return (
            <Box
              key={option._id}
              onClick={() =>
                !isAnswered &&
                !isSubmitting &&
                onAnswerChange(questionId, option.value)
              }
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: isAnswered || isSubmitting ? "default" : "pointer",
                p: 0.5,
                opacity: isAnswered || isSubmitting ? 0.9 : 1,
                pointerEvents: isSubmitting ? "none" : "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  minWidth: 32,
                  minHeight: 32,
                  borderRadius: "50%",
                  bgcolor: showCorrect
                    ? "success.main"
                    : showWrong
                    ? "error.main"
                    : highlightCorrect
                    ? "success.main"
                    : isSelected
                    ? "primary.main"
                    : "white",
                  border: "1px solid",
                  borderColor: showCorrect
                    ? "success.main"
                    : showWrong
                    ? "error.main"
                    : highlightCorrect
                    ? "success.main"
                    : isSelected
                    ? "primary.main"
                    : "black",
                  color:
                    showCorrect || showWrong || highlightCorrect || isSelected
                      ? "white"
                      : "black",
                  mr: 2,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: isAnswered
                      ? showCorrect
                        ? "success.main"
                        : showWrong
                        ? "error.main"
                        : highlightCorrect
                        ? "success.main"
                        : isSelected
                        ? "primary.main"
                        : "white"
                      : isSelected
                      ? "primary.main"
                      : "grey.200",
                  },
                }}
              >
                {optionChar}
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1">
                  {option.text}
                </Typography>
                {showCorrect && <CheckIcon color="success" fontSize="small" />}
                {showWrong && <CloseIcon color="error" fontSize="small" />}
                {highlightCorrect && (
                  <CheckIcon color="success" fontSize="small" />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </RadioGroup>
  );
};

// ─── Sub-component: Question Result Box ──────────────────────────────────────
const QuestionResultBox = ({ question, apiValidationResults }) => {
  const questionId = question._id;
  const validationResult = apiValidationResults[questionId];
  const isCorrect =
    question.type === "multiple-choice" &&
    (validationResult?.isCorrect || false);

  const bgColor =
    question.type === "short_answer"
      ? "info.light"
      : isCorrect
      ? "success.light"
      : "error.light";
  const borderColor =
    question.type === "short_answer"
      ? "info.main"
      : isCorrect
      ? "success.main"
      : "error.main";

  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        bgcolor: bgColor,
        borderColor,
        border: "2px solid",
        borderRadius: 2,
      }}
    >
      {question.type === "short_answer" ? (
        <>
          <Typography
            variant="body1"
            color="info.dark"
            fontWeight="bold"
            gutterBottom
          >
            Sample Answer:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {question.correctAnswer}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Note: Your answer may vary. Compare with the sample answer above.
          </Typography>
        </>
      ) : (
        <Typography
          variant="body1"
          color={isCorrect ? "success.dark" : "error.dark"}
          fontWeight="bold"
          gutterBottom
        >
          {isCorrect ? "✓ Correct!" : "✗ Incorrect"}
        </Typography>
      )}
      {question.explanation && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Explanation:</strong>{" "}
          <RichTextDisplay content={question.explanation} />
        </Typography>
      )}
    </Box>
  );
};

// ─── Main: VocabularyQuestionPanel ───────────────────────────────────────────
const VocabularyQuestionPanel = ({ exerciseId, selectedDay }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState({});
  const [apiValidationResults, setApiValidationResults] = useState({});
  const [questionsExpanded, setQuestionsExpanded] = useState(false);

  const { data: questionsResponse, isLoading: questionsLoading } =
    useGetStudentVocabularyQuestionsQuery(
      { exerciseId, dayId: selectedDay?._id },
      { skip: !exerciseId || !selectedDay?._id },
    );

  const [submitAnswer, { isLoading: isSubmitting }] = useSubmitAnswerMutation();

  const vocabularyQuestions = questionsResponse?.data || [];
  const currentQuestion = vocabularyQuestions[currentQuestionIndex];

  // Reset state when day changes
  useEffect(() => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults({});
    setApiValidationResults({});
    setQuestionsExpanded(false);
  }, [selectedDay?._id]);

  const handleNextQuestion = () => {
    if (currentQuestionIndex < vocabularyQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleAnswerChange = async (questionId, answer, type = null) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));

    // Don't auto-show results for fill-blank and short_answer types
    if (type === "fill-blank" || type === "short_answer") return;

    // For multiple-choice, call API to validate answer
    try {
      const response = await submitAnswer({
        exerciseId,
        dayId: selectedDay._id,
        questionId,
        answer,
        partType: "vocabulary",
      }).unwrap();

      setApiValidationResults((prev) => ({
        ...prev,
        [questionId]: {
          isCorrect: response.data.isCorrect,
          correctAnswer: response.data.correctAnswer,
          explanation: response.data.explanation,
        },
      }));

      setShowResults((prev) => ({ ...prev, [questionId]: true }));
    } catch (error) {
      console.error("Failed to submit answer:", error);
      toast.error("Failed to submit answer. Please try again.");
    }
  };

  const handleFillBlankChange = (questionId, position, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...(prev[questionId] || {}), [position]: value },
    }));
  };

  const handleCheckAnswer = (questionId) => {
    setShowResults((prev) => ({ ...prev, [questionId]: true }));
  };

  // ─── Loading state ───
  if (questionsLoading) {
    return (
      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Loading questions...
        </Typography>
      </Box>
    );
  }

  if (vocabularyQuestions.length === 0) {
    return (
      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No questions available for this vocabulary section.
        </Typography>
      </Box>
    );
  }

  if (!currentQuestion) return null;

  // ─── Render ───
  return (
    <Box sx={{ mt: 3 }}>
      {/* Question Header with Collapse Toggle */}
      <Paper
        elevation={1}
        sx={{
          mb: 2,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          bgcolor: "primary.light",
          "&:hover": {
            bgcolor: "primary.main",
            "& .MuiTypography-root, & .MuiChip-root": { color: "white" },
          },
          transition: "all 0.3s ease",
        }}
        onClick={() => setQuestionsExpanded(!questionsExpanded)}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="subtitle1" fontWeight="bold" color="white">
            Practice Questions ({vocabularyQuestions.length})
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: "white" }}>
          {questionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Paper>

      {/* Question Slide - Collapsible */}
      <Collapse in={questionsExpanded}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", mb: 2 }}>
              {currentQuestion.question && (
                <Typography variant="h6" fontWeight="bold" sx={{ flex: 1, pr: 2 }}>
                  {currentQuestion.question}
                </Typography>
              )}
              {currentQuestion.audioUrl && (
                <PreviewAudioButton audioUrl={currentQuestion.audioUrl} />
              )}
            </Box>

            {/* Media: Images & Audio */}
            <QuestionMedia question={currentQuestion} />

            {/* Render based on question type */}
            {currentQuestion.type === "fill-blank" ? (
              <FillBlankQuestion
                question={currentQuestion}
                answers={answers}
                showResults={showResults}
                onFillBlankChange={handleFillBlankChange}
                onCheckAnswer={handleCheckAnswer}
              />
            ) : currentQuestion.type === "short_answer" ? (
              <ShortAnswerQuestion
                question={currentQuestion}
                answers={answers}
                showResults={showResults}
                onAnswerChange={handleAnswerChange}
                onCheckAnswer={handleCheckAnswer}
              />
            ) : (
              <MultipleChoiceQuestion
                question={currentQuestion}
                answers={answers}
                showResults={showResults}
                apiValidationResults={apiValidationResults}
                isSubmitting={isSubmitting}
                onAnswerChange={handleAnswerChange}
              />
            )}

            {/* Loading indicator while submitting */}
            {isSubmitting && answers[currentQuestion._id] && (
              <Box
                sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <LinearProgress sx={{ flexGrow: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Checking answer...
                </Typography>
              </Box>
            )}

            {/* Result Box for non-fill-blank questions */}
            {showResults[currentQuestion._id] &&
              currentQuestion.type !== "fill-blank" && (
                <QuestionResultBox
                  question={currentQuestion}
                  apiValidationResults={apiValidationResults}
                />
              )}

            {/* Explanation for fill-blank questions */}
            {showResults[currentQuestion._id] &&
              currentQuestion.type === "fill-blank" &&
              currentQuestion.explanation && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    bgcolor: "info.light",
                    borderRadius: 2,
                    border: "1px solid ",
                    borderColor: "info.main",
                  }}
                >
                  <Typography variant="body2">
                    <strong>Explanation:</strong>{" "}
                    <RichTextDisplay content={currentQuestion.explanation} />
                  </Typography>
                </Box>
              )}
          </CardContent>
        </Card>

        {/* Question Navigation */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <PrevIcon
            sx={{
              color: "primary.dark",
              fontSize: "1.5rem",
              cursor: currentQuestionIndex === 0 ? "not-allowed" : "pointer",
              border: "1px solid rgb(17, 82, 147)",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              opacity: currentQuestionIndex === 0 ? 0.5 : 1,
            }}
            onClick={handlePrevQuestion}
          />
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            Question {currentQuestionIndex + 1} of {vocabularyQuestions.length}
          </Typography>
          <NextIcon
            sx={{
              color: "primary.dark",
              fontSize: "1.5rem",
              cursor: "pointer",
              border: "1px solid rgb(17, 82, 147)",
              borderRadius: "50%",
              width: "30px",
              height: "30px",
              opacity:
                currentQuestionIndex === vocabularyQuestions.length - 1
                  ? 0.5
                  : 1,
            }}
            onClick={handleNextQuestion}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default VocabularyQuestionPanel;
